import {
    Badge,
    classNames,
    IconNames,
    KeyButton,
    layout,
    MaterialIcon,
    PushButton,
    TextInput,
    theme,
    typography,
    useStorageItemValue,
    useTypeRef,
    View
} from "@variocube/cube-ui";
import * as React from "react";
import {Fragment, useEffect, useRef, useState} from "react";
import {keyframes, stylesheet} from "typestyle";
import {useLocalization} from "../../i18n";
import {Bookings} from "./booking";
import {em} from "csx";

interface BookingCodeDisplayProps {
    boxNumber: string;
    onNext: (code: string) => any;
    onBack: () => any;
}

export function BookingCodeManual({boxNumber, onNext, onBack}: BookingCodeDisplayProps) {
    const {t} = useLocalization();
    const bookings = useStorageItemValue<Bookings>("bookings");

    const input = useRef<HTMLInputElement | null>();
    const [type, setType] = useTypeRef();

    const [code, setCode] = useState('');
    const [invalidCode, setInvalidCode] = useState(false);
    const [confirmation, setConfirmation] = useState('');
    const [showDigits, setShowDigits] = useState(false);

    const verified = !!confirmation && code === confirmation;

    useEffect(() => {
        if (code.length > 0) {
            setInvalidCode(false);
        }

        // confirmation step
        if (confirmation) {
            if (code.length === confirmation.length) {
                if (code === confirmation) {
                    onNext(code);
                } else {
                    setCode('');
                    setInvalidCode(true);
                }
            }
            return;
        }

        // code selection step
        if (code.length === 4) {
            if (validCodeStrength(code)) {
                setConfirmation(boxNumber + code);
            } else {
                setInvalidCode(true);
            }
            setCode('');
        }
    }, [code])

    function handleBackButton() {
        // confirmation step
        if (confirmation) {
            setCode('');
            setConfirmation('');
            return;
        }

        // code selection step
        onBack();
    }

    function handleBackspace() {
        setCode(code.substring(0, code.length - 1));
    }

    function handleDigitsDisplay() {
        setShowDigits(!showDigits);
    }

    function unique(value: string, index: number, array: string[]) {
        return array.indexOf(value) === index;
    }

    function validCodeStrength(code: string) {
        // must not be empty and have a length of 4 digits
        if (!code || code.length !== 4) {
            return false;
        }
        // must not start with zero (e.g. 0123)
        if (code.indexOf("0") === 0) {
            return false;
        }
        // must not have all identical digits (e.g. 1111, 2222, 3333, etc.)
        if (Array.from(code).filter(unique).length === 1) {
            return false;
        }
        // must not be a set of 2 identical digits or a duo set of 2 digits (e.g. 1122, 2255, 1212, etc.)
        if (code.substring(0, 2) === code.substring(2)
            || (
                Array.from(code.substring(0, 2)).filter(unique).length === 1
                && Array.from(code.substring(2)).filter(unique).length === 1
            )
        ) {
            return false;
        }
        // must not be an increment or decrement sequence (e.g 1234, 6543, 7890, etc.)
        let consecutiveOccurrence = 0;
        for (let i = 0; i < code.length; i++) {
            if (i > 0) {
                let currentNumber = parseInt(code[i]);
                let previousNumber = parseInt(code[i - 1]);
                let increment = (previousNumber + 1 > 9) ? 0 : previousNumber + 1;
                let decrement = (previousNumber - 1 < 0) ? 0 : previousNumber - 1;
                if (currentNumber === increment || currentNumber === decrement) {
                    consecutiveOccurrence++;
                }
            }
        }
        if (consecutiveOccurrence === 3) {
            return false;
        }
        // must not fall into following special cases
        const forbiddenCodes = ["2580", "5683"];
        if (forbiddenCodes.includes(code)) {
            return false;
        }
        // must be unique across all other bookings
        if (bookings && bookings.hasOwnProperty(boxNumber + code)) {
            return false;
        }
        // code is "good enough"
        return true;
    }

    function renderDigit(digit: string) {
        if (digit !== '' && digit !== undefined) {
            return showDigits ? digit : "*";
        }
        return '';
    }

    return (
        <View className={classNames(layout.vertical, layout.paddingLarge, layout.spaceBetween)}>
            <div className={classNames(layout.horizontal, layout.center)}>
                <div className={classNames(theme.lessContrast, layout.vertical, layout.spaceBetween, styles.boxLeft)}>
                    <div className={styles.title}>
                        {confirmation ? t('BookingCodeManuallyConfirmation') : t("BookingManualBookingCode")}
                    </div>
                    <p>
                        {confirmation ? t('BookingCodeManuallyConfirmationDescription') : t("BookingManualBookingCodeDescription")}
                    </p>
                    <div className={classNames(layout.horizontal, layout.center, layout.alignCenter, styles.icons)}>
                        <div className={classNames(styles.iconContainer, typography.alignCenter)}>
                            <div className={styles.icon}>
                                <MaterialIcon icon={IconNames.box_number}/>
                            </div>
                            <div className={styles.iconText}>{t("BookingCodeManuallyBoxNumber")}</div>
                        </div>
                        <div>
                            +
                        </div>
                        <div className={classNames(styles.iconContainer, typography.alignCenter)}>
                            <div className={styles.icon}>
                                <MaterialIcon icon={IconNames.create}/>
                            </div>
                            <div className={styles.iconText}>{t("BookingCodeManuallyCode")}</div>
                        </div>
                    </div>
                    <div>
                        {t("BookingManualBookingCodeHint")}
                    </div>
                </div>
                <div className={classNames(theme.moreContrast, layout.vertical, layout.center, layout.alignCenter, layout.paddingLarge)}>
                    {verified && (
                        <div className={styles.verifiedOverlay}>
                            <div className={styles.verifiedOverlayContent}>
                                <div className={styles.checkIcon}>
                                    <MaterialIcon icon={IconNames.check}/>
                                </div>
                                <p>
                                    {t("BookingCodeCorrect")}
                                </p>
                            </div>
                        </div>
                    )}

                    {!verified && (
                        <Fragment>
                            <div className={classNames(layout.horizontal, styles.accessCodeInput)}>
                                {!confirmation && (
                                    <Fragment>
                                        {boxNumber.split('').map((digit, i) => (
                                            <div key={'boxNr-' + i} className={classNames(styles.digitBox)}>
                                                {digit}
                                            </div>
                                        ))}
                                        {Array.from({ length: 4 }, (_, i) => i).map(i => (
                                            <div key={'digit-' + i} className={classNames(styles.digitBox)}>
                                                {renderDigit(code[i])}
                                            </div>
                                        ))}
                                    </Fragment>
                                )}
                                {confirmation && (
                                    <Fragment>
                                        {Array.from({ length: confirmation.length }, (_, i) => i).map(i => (
                                            <div key={'digit-' + i} className={classNames(styles.digitBox)}>
                                                {renderDigit(code[i])}
                                            </div>
                                        ))}
                                    </Fragment>
                                )}
                            </div>

                            <TextInput
                                onChange={setCode}
                                value={code}
                                inputRef={input}
                                typeRef={setType}
                                className={styles.hidden}
                            />

                            <div className={classNames(styles.keyPad)}>
                                <div className={classNames(layout.horizontal, layout.spaceEvenly)}>
                                    <div className={classNames(layout.vertical, styles.fullGrow)}>
                                        <div className={layout.horizontal}>
                                            <KeyButton value="1" border={false} onType={type}/>
                                            <KeyButton value="2" border={false} onType={type}/>
                                            <KeyButton value="3" border={false} onType={type}/>
                                        </div>
                                        <div className={layout.horizontal}>
                                            <KeyButton value="4" border={false} onType={type}/>
                                            <KeyButton value="5" border={false} onType={type}/>
                                            <KeyButton value="6" border={false} onType={type}/>
                                        </div>
                                        <div className={layout.horizontal}>
                                            <KeyButton value="7" border={false} onType={type}/>
                                            <KeyButton value="8" border={false} onType={type}/>
                                            <KeyButton value="9" border={false} onType={type}/>
                                        </div>
                                    </div>
                                    <div className={classNames(layout.vertical, styles.halfGrow)}>
                                        <button onClick={handleBackspace} className={styles.iconButton}>
                                            <MaterialIcon icon={IconNames.backspace} className={styles.iconButtonIcon}/>
                                        </button>
                                        <button onClick={handleDigitsDisplay} className={styles.iconButton}>
                                            <MaterialIcon icon={showDigits ? IconNames.visibility_off : IconNames.visibility} className={styles.iconButtonIcon}/>
                                        </button>
                                        <KeyButton value="0" border={false} onType={type}/>
                                    </div>
                                </div>
                            </div>

                            {invalidCode && (
                                <Badge error>
                                    {confirmation && t('BookingCodeManuallyConfirmationError')}
                                    {!confirmation && t('BookingCodeManuallyCodeError')}
                                </Badge>
                            )}
                        </Fragment>
                    )}
                </div>
            </div>
            <div className={classNames(layout.horizontal, layout.spaceBetween)}>
                <PushButton onClick={handleBackButton}>{t("Back")}</PushButton>
            </div>
        </View>
    );
}

const styles = stylesheet({
    title: {
        fontSize: "1.5em",
    },
    boxLeft: {
        width: "20em",
        maxWidth: "20em",
        padding: "1.5em"
    },
    icons: {
        margin: "1rem 0 2rem 0",
    },
    iconContainer: {
        margin: "0 1.5rem",
        flex: "1",
    },
    icon: {
        fontSize: "3rem",
        lineHeight: 1,
    },
    iconText: {
        fontSize: "1.25rem",
        fontWeight: 500,
    },
    // code: {
    //     fontSize: "1.75em",
    //     fontWeight: "bold"
    // },
    keyPad: {
        fontSize: "1.75rem",
        width: '100%'
    },
    textInput: {
        fontWeight: "bold",
        width: "24rem",
        height: "4rem",
        fontSize: "1.75em"
    },
    fullGrow: {
        flexGrow: 1,
    },
    halfGrow: {
        flexGrow: 0.5
    },
    verifiedOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "var(--color-success)",
        animationName: keyframes({
            from: { opacity: 0 },
            to: { opacity: 1 }
        }),
        animationDelay: "0s",
        animationDuration: "0.5s",
        animationFillMode: "both",
    },
    verifiedOverlayContent: {
        display: "block",
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translateX(-50%) translateY(-50%)",
    },
    checkIcon: {
        fontSize: "4rem",
        lineHeight: 1,
    },
    hidden: {
        display: 'none'
    },
    accessCodeInput: {
        minWidth: '28rem',
        justifyContent: 'center',
        marginBottom: '1rem'
    },
    digitBox: {
        width: '3.5rem',
        height: '4rem',
        lineHeight: '1em',
        fontSize: '1.8rem',
        fontWeight: 'bold',
        padding: '0.3rem',
        border: '3px solid var(--color-foreground)',
        margin: '0 0.5rem 0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconButton: {
        border: 0,
        backgroundColor: "transparent",
        color: "inherit",
        padding: em(0.5),
        cursor: "pointer"
    },
    iconButtonIcon: {
        fontSize: em(1),
    }
});